import { Suspense, useState } from 'react';
import { useModifierKeys } from '@gonfalon/dom/useModifierKeys';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { useKeyCombo } from '@gonfalon/react/useKeyCombo';
import {
  Alert,
  DialogTrigger,
  IconButton,
  type IconButtonProps,
  Popover,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';

import { trackEnvironmentEvent } from '../analytics';
import { type SelectedEnvironment } from '../types';
import { useEventLocation } from '../utils/getEventLocation';

import { EnvironmentFallback } from './EnvironmentFallback';
import { SelectEnvironmentDialog } from './SelectEnvironmentDialog';

export function AddEnvironmentDialogTrigger({
  projectKey,
  excludedIds,
  onSelect,
  size = 'medium',
}: {
  projectKey: string;
  excludedIds: Set<string>;
  onSelect: (environment: SelectedEnvironment) => void;
  /** Defaults to the toolbar's `medium`. The flag list's column header packs this
   *  next to `small` actions, so it opts down to match and keep the row compact. */
  size?: IconButtonProps['size'];
}) {
  const keys = useModifierKeys();
  const [isOpen, setOpen] = useState(false);
  const location = useEventLocation();

  useKeyCombo('.', () => {
    setOpen(true);
  });

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={(open) => {
        setOpen(open);
        if (open) {
          trackEnvironmentEvent('View Another Environment Clicked', { location });
        }
      }}
    >
      <TooltipTrigger>
        <IconButton
          aria-label={msg('chrome.environmentSelector.viewAnother')}
          variant="minimal"
          size={size}
          icon="add"
        />
        <Tooltip placement="bottom">
          <Message id="chrome.environmentSelector.viewAnother" />
        </Tooltip>
      </TooltipTrigger>
      <Popover placement="bottom end">
        <Suspense fallback={<EnvironmentFallback />}>
          <ErrorBoundary
            severity="high"
            fallback={
              <Alert variant="inline" status="error">
                <Message id="chrome.environmentSelector.loadError" />
              </Alert>
            }
          >
            <SelectEnvironmentDialog
              projectKey={projectKey}
              excludedIds={excludedIds}
              onSelect={(v) => {
                trackEnvironmentEvent('Environment Selected', { environmentKey: v.key, location });
                !keys.current.shiftKey && setOpen(false);
                onSelect(v);
              }}
            />
          </ErrorBoundary>
        </Suspense>
      </Popover>
    </DialogTrigger>
  );
}
